// Client de l'API du cœur. Plus de jeton à saisir : la session est un cookie HttpOnly posé à la
// connexion, envoyé tout seul par le navigateur (même origine). Un 401 renvoie vers la connexion.

import type {
  Appareil, CorpsMarche, CorpsPesee, CorpsSeance, Etat, InfosInvitation, ItemAgenda, JourPortail, Moi, Notification,
  PortailOuvert, Rappel, Reponse,
} from '@alfred/shared';

import { viderFile } from './fileActions';
import { ENTETE_HORS_LIGNE, effacerDonnees } from './horsLigne';

export class NonConnecte extends Error {}
/** Le réseau manque, ou le serveur ne répond plus : une écriture n'est pas partie (ou sa réponse s'est perdue). */
export class HorsLigne extends Error {}

const MESSAGE_HORS_LIGNE = 'Hors ligne : rien n’est parti. Réessayez quand le réseau revient.';

async function appel<T>(methode: string, chemin: string, corps?: unknown, idAction?: string): Promise<T> {
  let r: Response;
  try {
    r = await fetch(chemin, {
      method: methode,
      credentials: 'same-origin',
      ...(corps !== undefined || idAction
        ? { headers: { ...(corps !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(idAction ? { 'X-Alfred-Action': idAction } : {}) } }
        : {}),
      ...(corps !== undefined ? { body: JSON.stringify(corps) } : {}),
    });
  } catch {
    // Pas de réseau (et, pour une lecture, rien de gardé par le service worker, lot 6.3).
    if (methode === 'GET') throw new Error('Alfred est injoignable.');
    throw new HorsLigne(MESSAGE_HORS_LIGNE);
  }
  if (methode !== 'GET' && [502, 503, 504].includes(r.status)) throw new HorsLigne(MESSAGE_HORS_LIGNE);
  // Une réponse gardée par le service worker dit sa date : l'app affiche « Hors ligne » (App.tsx). Absente : en ligne.
  window.dispatchEvent(new CustomEvent<string | null>('alfred:reseau', { detail: r.headers.get(ENTETE_HORS_LIGNE) }));
  if (r.status === 401 && !chemin.startsWith('/api/auth/connexion')) throw new NonConnecte('non connecté');
  if (r.status === 204) return undefined as T;
  const donnees = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) {
    // Une notice à accepter que cette version de l'app ne sait pas montrer : c'est qu'elle est périmée (main.tsx).
    if (r.status === 403 && (donnees as { consentement?: boolean }).consentement) window.dispatchEvent(new Event('alfred:version-perimee'));
    throw new Error(donnees.error ?? `erreur ${r.status}`);
  }
  return donnees;
}

/** Réglages → Aussi sur ntfy (lot 6.6). `briefSeul` : la cible d'avant, qui ne reçoit que le brief. */
export interface ReglageNtfy {
  serveur: { url: string; topic: string; aJeton: boolean; briefSeul: boolean } | null;
  cloud: boolean;
}

/** Réglages → Connexions avancées (lots 7.2 et 7.4). */
export interface ReglagesMcp {
  sansValidation: string[];
  dispensables: string[];
  /** L'adresse du MCP à coller dans claude.ai ou ChatGPT. */
  adresse: string;
  /** Vrai sur le Pi : la page d'accord s'ouvre sur l'adresse privée d'Alfred (Tailscale). */
  accordPrive: boolean;
}

export interface Brief {
  date: string;
  aujourdhui: ItemAgenda[];
  enRetard: ItemAgenda[];
  aVenir: ItemAgenda[];
  erreurs: string[];
  alertes: string[];
  titre: string;
  texte: string;
}

export interface Agenda {
  date: string;
  ouverts: PortailOuvert[];
  jours: JourPortail[];
  alertes: string[];
  erreurs: string[];
}

export interface ResumeNote {
  id: string;
  type: string;
  chemin: string;
  titre: string;
  partage: string | null;
  majLe: string;
  seq: number;
}

export interface Note extends ResumeNote {
  contenu: string;
  frontmatter: Record<string, unknown>;
  creeLe: string;
}

export interface Demande {
  id: string;
  type: 'autorisation' | 'proposition';
  titre: string;
  detail: string;
  auteur: string;
  statut: 'en_attente' | 'acceptee' | 'refusee' | 'expiree';
  creeLe: string;
}

export interface Connexion {
  id: string;
  type: string;
  fournisseur: string;
  nom: string;
  etat: 'ok' | 'a_reconnecter' | 'erreur';
  erreur: string | null;
  erreurDepuis: string | null;
  derniereSynchroLe: string | null;
  /** Synchronisée par le socle (iCloud…) ; Withings, non. */
  geree: boolean;
  /** Que faire, quand le fournisseur refuse le mot de passe. */
  conseil: string | null;
  calendriers: string[];
  /** Un agenda par adresse (lot 4.4) : son serveur, et s'il est déclaré de confiance. */
  hote?: string;
  confiance?: boolean;
  /** Un dossier de documents (lot 4.5) : son chemin dans le dossier des documents. */
  chemin?: string;
  /** Une boîte mail : son adresse et les dossiers relevés. */
  adresse?: string;
  dossiers?: string[];
}

export interface DocumentIndexe {
  id: number;
  nom: string;
  dossier: string;
  extension: string;
  taille: number;
  modifieLe: string;
  /** L'état de la lecture du texte : 'lu', 'a_lire', 'sans_texte'… */
  texte: string;
  /** Quelques mots autour de ce qui a été trouvé dans le texte. */
  passage: string | null;
}

/** La notice de confidentialité de l'instance (lot 5.2). */
export interface Notice {
  version: number;
  texte: string;
  requise: boolean;
}

/** Le rapport de diagnostic (lot 5.6), tel que la personne le relit avant de l'envoyer. */
export interface RapportDiagnostic {
  version: string;
  notifications: { abonnements: number };
  connexions: { type: string; fournisseur: string; etat: string; erreur: string | null; derniere_synchro_le: string | null }[];
  taches_en_echec: { type: string; debut: string; resume: string }[];
}

export interface FournisseurMail {
  id: string;
  nom: string;
  motDePasseApplication: boolean;
  relevable: boolean;
  aide: string;
}

export interface MailReleve {
  id: number;
  de: string;
  sujet: string;
  recuLe: string;
  extrait: string;
  indesirable: boolean;
  /** Le classement de fond (lot 4.3) : null tant qu'il n'est pas passé. */
  categorie: 'important' | 'a_faire' | 'info' | 'publicite' | null;
  resume: string | null;
  important: boolean;
}

// --- l'assistant (lot 3.3) ---------------------------------------------------------------

export interface ActionFaite {
  nom: string;
  ecriture: boolean;
  resume: string;
  /** L'écriture attend la validation de la personne (l'échange avait lu un contenu externe). */
  demande?: string;
}

export interface EtatIA {
  disponible: boolean;
  modele: string | null;
  enAttente: { interactif: number; fond: number };
  consommationMois: number;
  plafondMois: number;
  membre30j: { demandes: number; jetons: number; dureeMoyenneMs: number };
  debogageJusquA: string | null;
}

export interface LigneJournal {
  id: string;
  le: string;
  acteur: string;
  outil: string;
  resume: string;
  erreur: string | null;
  raison: string | null;
  suiteANonFiable: boolean;
  demande: { id: string; statut: string | null } | null;
}

export interface Conversation {
  id: string;
  titre: string;
  creeLe: string;
  majLe: string;
}

export interface MessageChat {
  id: string;
  role: 'user' | 'assistant';
  contenu: string;
  actions: ActionFaite[];
  le: string;
}

export type CategorieSouvenir = 'fait' | 'preference' | 'interdit';
export type ModeMemoire = 'demander' | 'retenir' | 'rien';

export interface Souvenir {
  id: string;
  texte: string;
  categorie: CategorieSouvenir;
  statut: 'propose' | 'confirme' | 'rejete';
  source: string;
  creeLe: string;
  majLe: string;
}

// --- l'entretien d'accueil (lot 3.5) ---------------------------------------------------------

export interface EtatTheme {
  numero: number;
  titre: string;
  duree: number;
  poids: number;
  obligatoire: boolean;
  disponible: boolean;
  fait: boolean;
  /** Le cerveau en contient déjà la matière (un cerveau importé). */
  couvert: boolean;
}

export interface TableauEntretien {
  score: number;
  themes: EtatTheme[];
  suggestion: { numero: number; texte: string } | null;
  assistant: string;
}

export interface OptionQuestion {
  valeur: string;
  libelle: string;
  libelle_tu?: string;
  exemple?: string;
  champ?: 'nom' | 'texte' | 'jour';
}

export interface QuestionEntretien {
  id: string;
  texte: string;
  texte_tu: string;
  aide?: string;
  aide_tu?: string;
  type: 'texte' | 'texte_long' | 'choix' | 'cases' | 'heure_variante' | 'jours' | 'horaires' | 'moment' | 'plages' | 'nom_complet' | 'date_annuelle'
    | 'date' | 'habitudes' | 'animaux' | 'taches' | 'echeances';
  /** La réponse est lue par l'IA, en arrière-plan. */
  ia: boolean;
  obligatoire: boolean;
  options: OptionQuestion[];
  defaut?: string | { depuis: string; decalage: number };
  jours: boolean;
  /** Fait partie de la boucle du thème (un proche, un projet à la fois). */
  boucle: boolean;
  si?: Record<string, string>;
  prerempli: unknown;
}

export interface ThemeEntretien {
  numero: number;
  titre: string;
  intro: string;
  intro_tu: string;
  obligatoire: boolean;
  questions: QuestionEntretien[];
  assistant: string;
  registre: 'vous' | 'tu';
  prenom: string;
  /** « personne » : les questions se répètent pour chaque proche. */
  boucle: string | null;
  /** Les proches déjà connus du cerveau. */
  connus: string[];
}

/** La question du jour : une question de l'entretien, ou un thème à commencer. */
export type QuestionDuJour =
  | { genre: 'question'; id: string; numero: number; texte: string; question: QuestionEntretien }
  | { genre: 'theme'; id: string; numero: number; titre: string; duree: number; texte: string };

export interface LigneRetenue {
  cle: string;
  genre: 'profil' | 'reglage' | 'tache' | 'souvenir' | 'fiche' | 'rappel';
  texte: string;
}

/** Les évènements du flux de /api/chat. */
export type EvenementChat =
  | { type: 'conversation'; id: string }
  | { type: 'texte'; texte: string }
  | { type: 'annule' }
  | ({ type: 'outil' } & ActionFaite)
  | { type: 'fin'; messageId: string; modele: string; jetons: number; dureeMs: number }
  | { type: 'indisponible'; raison: string; message: string; essentiel: string };

/**
 * Un échange avec Alfred, en flux (SSE sur un POST : EventSource ne sait faire que des GET). Chaque évènement est
 * passé à `surEvenement` dès qu'il arrive ; la promesse se résout à la fin du flux.
 */
async function discuter(corps: { conversationId?: string; message: string }, surEvenement: (e: EvenementChat) => void): Promise<void> {
  const r = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(corps),
  });
  if (r.status === 401) throw new NonConnecte('non connecté');
  if (!r.ok || !r.body) {
    const d = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(d.error ?? `erreur ${r.status}`);
  }
  const lecteur = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let reste = '';
  for (;;) {
    const { value, done } = await lecteur.read();
    if (done) break;
    reste += value;
    let fin: number;
    while ((fin = reste.indexOf('\n\n')) >= 0) {
      const bloc = reste.slice(0, fin);
      reste = reste.slice(fin + 2);
      const type = /^event: (.+)$/m.exec(bloc)?.[1];
      const donnees = /^data: (.+)$/m.exec(bloc)?.[1];
      if (type && donnees) surEvenement({ type, ...(JSON.parse(donnees) as object) } as EvenementChat);
    }
  }
}

export const api = {
  // --- compte ---------------------------------------------------------------------
  moi: () => appel<Moi>('GET', '/api/moi'),
  connexion: (email: string, motDePasse: string) => appel<{ ok: true }>('POST', '/api/auth/connexion', { email, motDePasse }),
  // Se déconnecter efface aussi ce que l'appareil gardait pour le hors-ligne, et sa file d'attente (lots 6.3, 6.5).
  deconnexion: () => appel<void>('POST', '/api/auth/deconnexion').finally(() => Promise.all([effacerDonnees().catch(() => undefined), viderFile()])),
  invitation: (code: string) => appel<InfosInvitation>('POST', '/api/invitations/consulter', { code }),
  accepter: (c: { code: string; prenom: string; email: string; motDePasse: string; nomFoyer?: string; consentement?: number }) => appel<{ ok: true }>('POST', '/api/invitations/accepter', c),
  notice: () => appel<Notice>('GET', '/api/notice'),
  diagnostic: () => appel<RapportDiagnostic>('GET', '/api/diagnostic'),
  envoyerDiagnostic: (message: string) => appel<{ id: string }>('POST', '/api/diagnostic', { message }),
  supprimerMonCompte: (motDePasse: string) => appel<void>('POST', '/api/moi/suppression', { motDePasse }),
  consentir: (version: number) => appel<{ version: number }>('POST', '/api/notice/consentement', { version }),
  reinitialiser: (code: string, motDePasse: string) => appel<void>('POST', '/api/reinitialisations', { code, motDePasse }),

  // --- santé (repris de la console Brain) ---------------------------------------------
  etat: () => appel<Etat>('GET', '/api/sante/etat'),
  seance: (c: CorpsSeance, idAction?: string) => appel<Reponse>('POST', '/api/sante/seance', c, idAction),
  marche: (c: CorpsMarche, idAction?: string) => appel<Reponse>('POST', '/api/sante/marche', c, idAction),
  pesee: (c: CorpsPesee) => appel<Reponse>('POST', '/api/sante/pesee', c),
  annuler: (id: string) => appel<Reponse>('POST', '/api/sante/annuler', { id }),
  activerSante: () => appel<{ actif: boolean }>('POST', '/api/sante/activer', {}),
  connecterWithings: () => appel<{ url: string }>('POST', '/api/sante/withings/connexion'),

  // --- agenda, rappels, brief ---------------------------------------------------------
  brief: () => appel<Brief>('GET', '/api/brief'),
  agenda: (jours = 14) => appel<Agenda>('GET', `/api/agenda?jours=${jours}`),
  rappels: () => appel<Rappel[]>('GET', '/api/rappels'),
  /** Le rappel en une phrase (lot 6.4) : « appeler le garage demain à 9h ». */
  rappelRapide: (texte: string, le: string, idAction: string) => appel<{ rappel: Rappel; resume: string }>('POST', '/api/rappels/rapide', { texte, le }, idAction),
  /** Renvoie une action de la file (lot 6.5) : le statut compte, pas le corps ; le réseau absent lève une erreur. */
  renvoyer: async (chemin: string, corps: unknown, idAction: string): Promise<{ statut: number; raison?: string }> => {
    const r = await fetch(chemin, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Alfred-Action': idAction }, body: JSON.stringify(corps) });
    const raison = r.ok ? undefined : ((await r.json().catch(() => ({}))) as { error?: string }).error;
    return { statut: r.status, ...(raison ? { raison } : {}) };
  },
  creerRappel: (r: Partial<Rappel> & { genre: 'rappel' | 'date'; titre: string; date: string }) => appel<Rappel>('POST', '/api/rappels', r),
  rappelFait: (id: string, jour: string, idAction?: string) => appel<{ occurrence: string; resume: string }>('POST', `/api/rappels/${id}/fait`, { jour }, idAction),
  annulerRappel: (occurrence: string) => appel<Rappel>('POST', `/api/rappels/occurrences/${occurrence}/annuler`),
  reglerBilan: (actif: boolean, heure: string) => appel<{ actif: boolean; heure: string }>('PUT', '/api/bilan/reglage', { actif, heure }),
  agendaConfiance: () => appel<{ confiance: boolean }>('GET', '/api/agenda/confiance'),
  reglerAgendaConfiance: (confiance: boolean) => appel<{ confiance: boolean }>('PUT', '/api/agenda/confiance', { confiance }),
  reglerBrief: (actif: boolean, heure: string) => appel<{ actif: boolean; prochain?: string | null }>('PUT', '/api/brief/reglage', { actif, heure }),

  // --- cerveau -----------------------------------------------------------------------
  notes: (q?: string) => appel<ResumeNote[]>('GET', q ? `/api/notes?q=${encodeURIComponent(q)}` : '/api/notes'),
  note: (id: string) => appel<Note>('GET', `/api/notes/${id}`),
  liensNote: (id: string) => appel<{ cible: string; id: string | null }[]>('GET', `/api/notes/${id}/liens`),
  creerNote: (type: string, titre: string) => appel<Note>('POST', '/api/notes', { type, titre }),
  modifierNote: (id: string, contenu: string, seqAttendu: number) => appel<Note>('PUT', `/api/notes/${id}`, { contenu, seqAttendu }),
  supprimerNote: (id: string) => appel<void>('DELETE', `/api/notes/${id}`),

  // --- à valider ----------------------------------------------------------------------
  demandes: () => appel<Demande[]>('GET', '/api/demandes?statut=en_attente'),
  repondre: (id: string, accepte: boolean) => appel<Demande>('POST', `/api/demandes/${id}/reponse`, { accepte }),

  // --- notifications ------------------------------------------------------------------
  notifications: () => appel<Notification[]>('GET', '/api/notifications'),
  marquerLue: (id: string) => appel<void>('POST', `/api/notifications/${encodeURIComponent(id)}/lue`),
  toutMarquerLu: () => appel<{ marquees: number }>('POST', '/api/notifications/lues'),
  cleVapid: async () => (await appel<{ cle: string }>('GET', '/api/notifications/cle-publique')).cle,
  abonner: (sub: PushSubscriptionJSON) => appel<void>('POST', '/api/notifications/abonnements', { endpoint: sub.endpoint, keys: sub.keys }),
  desabonner: (endpoint: string) => appel<void>('DELETE', '/api/notifications/abonnements', { endpoint }),
  reglageNtfy: () => appel<ReglageNtfy>('GET', '/api/notifications/ntfy'),
  reglerNtfy: (url: string, topic: string, jeton?: string) => appel<ReglageNtfy>('PUT', '/api/notifications/ntfy', { url, topic, ...(jeton ? { jeton } : {}) }),
  retirerNtfy: () => appel<void>('DELETE', '/api/notifications/ntfy'),
  essaiNtfy: () => appel<void>('POST', '/api/notifications/ntfy/essai'),
  testerPush: () => appel<{ appareils: number }>('POST', '/api/notifications/essai'),

  // --- l'assistant ------------------------------------------------------------------
  oauthClient: (clientId: string, redirectUri: string) =>
    appel<{ nom: string; portees: string[] }>('GET', `/api/oauth/client?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
  oauthAutoriser: (c: { client_id: string; redirect_uri: string; code_challenge: string; state?: string; portees: string[]; accepte: boolean }) =>
    appel<{ redirection: string }>('POST', '/api/oauth/autoriser', c),
  reglagesMcp: () => appel<ReglagesMcp>('GET', '/api/mcp/reglages'),
  reglerMcp: (sansValidation: string[]) => appel<ReglagesMcp>('PUT', '/api/mcp/reglages', { sansValidation }),
  etatIA: () => appel<EtatIA>('GET', '/api/ia/etat'),
  reglerDebogage: (actif: boolean) => appel<{ debogageJusquA: string | null }>('PUT', '/api/ia/debogage', { actif }),
  journal: (limite = 100) => appel<LigneJournal[]>('GET', `/api/journal?limite=${limite}`),
  conversations: () => appel<Conversation[]>('GET', '/api/conversations'),
  conversation: (id: string) => appel<{ conversation: Conversation; messages: MessageChat[] }>('GET', `/api/conversations/${id}/messages`),
  discuter,
  entretien: () => appel<TableauEntretien>('GET', '/api/entretien'),
  themeEntretien: (n: number) => appel<ThemeEntretien>('GET', `/api/entretien/themes/${n}`),
  apercuEntretien: (n: number, c: { reponses?: Record<string, unknown>; passees?: string[]; boucle?: Record<string, unknown>[]; extractions?: Record<string, string> }) =>
    appel<{ lignes: LigneRetenue[] }>('POST', `/api/entretien/themes/${n}/apercu`, c),
  enregistrerEntretien: (n: number, c: { reponses?: Record<string, unknown>; passees?: string[]; boucle?: Record<string, unknown>[]; extractions?: Record<string, string>; decochees: string[] }) =>
    appel<TableauEntretien>('POST', `/api/entretien/themes/${n}/enregistrer`, c),
  questionDuJour: () => appel<{ question: QuestionDuJour | null; registre?: 'vous' | 'tu'; prenom?: string; assistant?: string }>('GET', '/api/entretien/question-du-jour'),
  apercuQuestionDuJour: (reponse: unknown, extraction?: string) => appel<{ lignes: LigneRetenue[] }>('POST', '/api/entretien/question-du-jour/apercu', { reponse, extraction }),
  enregistrerQuestionDuJour: (reponse: unknown, extraction: string | undefined, decochees: string[]) =>
    appel<TableauEntretien>('POST', '/api/entretien/question-du-jour/enregistrer', { reponse, extraction, decochees }),
  ignorerQuestionDuJour: () => appel<void>('POST', '/api/entretien/question-du-jour/ignorer', {}),
  extraireEntretien: (question: string, texte: string, sujet: string) => appel<{ id: string }>('POST', '/api/entretien/extraire', { question, texte, sujet }),
  souvenirs: () => appel<{ mode: ModeMemoire; souvenirs: Souvenir[] }>('GET', '/api/souvenirs'),
  ajouterSouvenir: (texte: string, categorie: CategorieSouvenir) => appel<Souvenir>('POST', '/api/souvenirs', { texte, categorie }),
  changerSouvenir: (id: string, c: { texte?: string; categorie?: CategorieSouvenir; statut?: 'confirme' | 'rejete' }) => appel<Souvenir>('PUT', `/api/souvenirs/${id}`, c),
  oublierSouvenir: (id: string) => appel<void>('DELETE', `/api/souvenirs/${id}`),
  reglerMemoire: (mode: ModeMemoire) => appel<{ mode: ModeMemoire }>('PUT', '/api/souvenirs/mode', { mode }),

  // --- réglages -----------------------------------------------------------------------
  appareils: () => appel<Appareil[]>('GET', '/api/appareils'),
  revoquerAppareil: (id: string) => appel<void>('DELETE', `/api/appareils/${id}`),
  creerAppareil: (type: string, nom: string, portee: string[]) => appel<{ id: string; jeton: string }>('POST', '/api/appareils', { type, nom, portee }),
  connexions: () => appel<Connexion[]>('GET', '/api/connexions'),
  connecterIcloud: (utilisateur: string, motDePasse: string) => appel<{ id: string }>('POST', '/api/connexions/icloud', { utilisateur, motDePasse }),
  fournisseurMail: (domaine: string) => appel<{ fournisseur: FournisseurMail | null }>('GET', `/api/mails/fournisseur?domaine=${encodeURIComponent(domaine)}`),
  connecterBoite: (b: { adresse: string; motDePasse: string; hote?: string }) => appel<{ id: string; resume: string }>('POST', '/api/connexions/imap', b),
  mails: (limite = 5, categorie?: string) => appel<MailReleve[]>('GET', `/api/mails?limite=${limite}${categorie ? `&categorie=${categorie}` : ''}`),
  connecterIcs: (url: string, nom: string) => appel<{ id: string; resume: string }>('POST', '/api/connexions/ics', { url, nom }),
  reglerConfianceConnexion: (id: string, confiance: boolean) => appel<{ confiance: boolean }>('PUT', `/api/connexions/${id}/confiance`, { confiance }),
  dossiersDocuments: (chemin: string) => appel<{ disponible: boolean; dossiers: string[] }>('GET', `/api/documents/dossiers?chemin=${encodeURIComponent(chemin)}`),
  indexerDossier: (chemin: string, nom: string) => appel<{ id: string; resume: string }>('POST', '/api/connexions/dossier', { chemin, nom }),
  confirmerDisparitions: (id: string) => appel<{ resume: string }>('POST', `/api/connexions/${id}/confirmer-disparitions`),
  documents: (q: string) => appel<DocumentIndexe[]>('GET', `/api/documents?q=${encodeURIComponent(q)}`),
  supprimerConnexion: (id: string) => appel<void>('DELETE', `/api/connexions/${id}`),
  synchroniserConnexion: (id: string) => appel<{ resume: string }>('POST', `/api/connexions/${id}/synchroniser`),
  reconnecterConnexion: (id: string, secret: string) => appel<{ resume: string }>('PUT', `/api/connexions/${id}/secret`, { secret }),
  inviter: (email?: string) => appel<{ lien: string | null; expireLe: string }>('POST', '/api/foyer/invitations', email ? { email } : {}),
};
